import TabContentWrapper from 'pages/Profile/components/TabContentWrapper';
import styles from './styles.module.scss';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Spinner, SearchFieldWithDebounce } from 'components';
import { showErrorMessage, showSuccessMessage, priceConverter } from 'helpers';
import PropertiesTable from './PropertiesTable';
import { Icons } from 'pages/Profile/components/icons';
import { getPropertyReferenceData, updateClientProperty } from 'api/radarPortfolio';
import moment from 'moment';

const Portfolio = ({ contactId }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [archived, setArchived] = useState<boolean>(false);
  const [archivesLoaded, setArchivesLoaded] = useState<boolean>(false);
  const [properties, setProperties] = useState<any[]>([]);
  const [archivedProperties, setArchivedProperties] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  if (!contactId) return null;

  const adjustedValues = {
    homeValue: 'homeAdjustedValue',
    rentalValue: 'rentalAdjustedValue',
  };

  useEffect(() => {
    fetchPortfolio(false);
  }, []);

  const getPropertyAddress = (address) => {
    try {
      const { address1, parsedData, address: addressObj } = address;

      if (address1) {
        if (!address1.toLowerCase().includes('undefined')) return address1;
      }

      if (parsedData) {
        const { address1: parsedAddress1 } = parsedData;
        if (parsedAddress1) return parsedAddress1;
      }

      if (addressObj) {
        const { Line1, delivery_line_1 } = addressObj;
        if (Line1) return Line1;

        if (delivery_line_1) return delivery_line_1;
      }

      return '-';
    } catch (e) {
      return '-';
    }
  };

  const getLocationAddress = (address) => {
    try {
      const { address2, parsedData, address: addressObj } = address;

      if (address2) {
        if (!address2.toLowerCase().includes('undefined')) return address2;
      }

      if (parsedData) {
        const { address2: parsedAddress2 } = parsedData;
        if (parsedAddress2) return parsedAddress2;
      }

      if (addressObj) {
        const { City, State, Zip, components } = addressObj;
        if (City) {
          let location = City;
          if (State) location += `, ${State}`;
          if (Zip) location += `, ${Zip}`;
          return location;
        }

        if (components.last_line) return;
      }
      return '-';
    } catch (e) {
      return '-';
    }
  };

  const fetchPortfolio = async (archived) => {
    setLoading(true);
    if (archived) setArchivesLoaded(true);
    try {
      const response = await getPropertyReferenceData({ Archive: archived, MemberId: contactId });
      if (response.status === 200 && response.data?.length) {
        const responseProperties =
          response.data.flatMap(
            (item) =>
              item.Client?.Address?.map((clientAddress) => {
                const { address1, address2 } = clientAddress.parsedData || clientAddress || {};
                const homeValue = getHouseValueRange(clientAddress.parsedData, 'homeValue');
                const rentalValue = getHouseValueRange(clientAddress.parsedData, 'rentalValue');
                return {
                  id: item.Id,
                  property: { line1: address1, line2: address2 },
                  addressId: clientAddress.Id,
                  clientId: item.ClientId,
                  clientFirstName: item.FirstName,
                  clientLastName: item.LastName,
                  clientName: item.FirstName + `${item.LastName ? ` ${item.LastName}` : ''}`,
                  clientAvatarUrl: item.AvatarUrl,
                  homeValue,
                  rentalValue,
                  activity: clientAddress.neighbourhoodRadius || '-',
                  closed: clientAddress.closingDate
                    ? moment(clientAddress.closingDate).format('MM/DD/YYYY')
                    : '-',
                  isArchived: archived,
                  propertyPhotoUrl: null,
                  propertyAddress: getPropertyAddress(clientAddress),
                  locationAddress: getLocationAddress(clientAddress),
                  itemsStringified: `${item.FirstName}${item.LastName ? ` ${item.LastName}` : ''}${
                    homeValue || ''
                  }${rentalValue || ''}${
                    clientAddress.closingDate
                      ? moment(clientAddress.closingDate).format('MM/DD/YYYY')
                      : ''
                  }${getPropertyAddress(clientAddress)}${getLocationAddress(clientAddress)}${
                    clientAddress.neighbourhoodRadius || ''
                  }`,
                };
              }) || [],
          ) ?? [];
        archived ? setArchivedProperties(responseProperties) : setProperties(responseProperties);
      }
    } catch (e) {
      showErrorMessage('Unable to fetch agent portfolio');
    }
    setLoading(false);
  };

  const getFormattedDate = (parsedData, key) => {
    const { month, year } = parsedData?.[key] || {};
    return month && year ? moment(`${year}-${month}`, 'YYYY-M').format('YYYY-MM') : null;
  };

  const getHouseValueRange = (data, key) => {
    const attomDate = getFormattedDate(data, 'attom');
    const houseCanaryDate = getFormattedDate(data, 'houseCanary');
    const manualDate = getFormattedDate(data, 'manual');

    let isValidAttom = false;
    let isValidHouseCanary = false;
    let isValidManual = false;

    const dates: any = [
      { key: 'attom', date: attomDate },
      { key: 'houseCanary', date: houseCanaryDate },
      { key: 'manual', date: manualDate },
    ].filter((d) => d.date);

    if (dates.length > 0) {
      const latestDate = dates.reduce((a, b) => (a.date > b.date ? a : b)).date;
      isValidAttom = attomDate === latestDate;
      isValidHouseCanary = houseCanaryDate === latestDate;
      isValidManual = manualDate === latestDate;
    }

    const attomValue = isValidAttom
      ? Number(data?.attom?.[adjustedValues?.[key]] || data?.attom?.[key])
      : null;
    const houseCanaryValue = isValidHouseCanary
      ? Number(data?.houseCanary?.[adjustedValues?.[key]] || data?.houseCanary?.[key])
      : null;
    const manualValue = isValidManual ? Number(data?.manual?.[key]) : null;

    const values: any = [attomValue, houseCanaryValue, manualValue].filter((v) => v);

    if (values.length > 1) {
      const min = Math.min(...values);
      const max = Math.max(...values);
      if (min === max) {
        return min.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
        });
      }
      return `${priceConverter(min)} - ${priceConverter(max)}`;
    } else if (attomValue) {
      return attomValue.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      });
    } else if (houseCanaryValue) {
      return houseCanaryValue.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      });
    } else if (manualValue) {
      return manualValue.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      });
    } else {
      return '-';
    }
  };

  const handlePortfolioItemArchive = async (
    clientId: number,
    addressId: number,
    isArchive: boolean,
  ) => {
    try {
      const response = await updateClientProperty({
        clientId,
        addressId,
        isArchive,
      });
      if (response.status === 200) {
        if (isArchive) {
          const found = properties.find(
            (property) => property.clientId === clientId && property.addressId === addressId,
          );
          if (found) {
            setProperties((prev) =>
              prev.filter(
                (property) => property.clientId !== clientId && property.addressId !== addressId,
              ),
            );
            setArchivedProperties((prev) => [
              ...prev,
              {
                ...found,
                isArchived: true,
              },
            ]);
          }
        } else {
          const found = archivedProperties.find(
            (property) => property.clientId === clientId && property.addressId === addressId,
          );
          if (found) {
            setArchivedProperties((prev) =>
              prev.filter(
                (property) => property.clientId !== clientId && property.addressId !== addressId,
              ),
            );
            setProperties((prev) => [
              ...prev,
              {
                ...found,
                isArchived: false,
              },
            ]);
          }
        }
        showSuccessMessage(`Portfolio item  ${isArchive ? 'archived' : 'unarchived'}`);
      }
    } catch (e) {
      showErrorMessage(`Unable to ${isArchive ? 'archive' : 'unarchive'} portfolio item.`);
    }
  };

  if (loading)
    return (
      <TabContentWrapper setMargin contentHeightFull>
        <div className={styles.portfolio}>
          <div className={classNames(styles.content, styles.contentLoading)}>
            <Spinner />
          </div>
        </div>
      </TabContentWrapper>
    );
  else if (!loading && !properties.length)
    return (
      <TabContentWrapper setMargin contentHeightFull>
        <div className={styles.portfolio}>
          <div className={styles.content}>
            <div className={styles.empty}>No portfolio property found.</div>
          </div>
        </div>
      </TabContentWrapper>
    );

  return (
    <TabContentWrapper setMargin contentHeightFull>
      <div className={styles.portfolio}>
        <div className={styles.header}>
          <div className={styles.left}>
            Properties
            <span>{` (${archived ? archivedProperties.length || 0 : properties.length})`}</span>
          </div>
          <div className={styles.right}>
            <SearchFieldWithDebounce
              className={styles.searchField}
              resetAction={() => setSearchQuery('')}
              doNotDispatch
              sendAction={(value: string) => setSearchQuery(value)}
              placeholder="Search"
              isExpandedVersion
            />
            <div
              className={classNames(styles.icon, archived ? styles.iconSelected : undefined)}
              onClick={() => {
                setArchived((prev) => !prev);
                if (!archivesLoaded) fetchPortfolio(true);
              }}
            >
              <Icons variant={Icons.ARCHIVE} />
            </div>
          </div>
        </div>
        <div className={styles.content}>
          <PropertiesTable
            searchQuery={searchQuery}
            handlePortfolioItemArchive={handlePortfolioItemArchive}
            properties={archived ? archivedProperties ?? [] : properties}
          />
        </div>
      </div>
    </TabContentWrapper>
  );
};

export default Portfolio;
